import {BrowserRouter, Routes, Route, Link, useLocation} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useEffect} from 'react';
import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {
  IconHome,
  IconUsers,
  IconBuildingStore,
  IconSettings,
  IconShoppingBag,
  IconBoxSeam,
  IconDashboard,
  IconUserPlus,
  IconShoppingCart,
  IconCrown,
  IconMessage,
  IconSparkles,
  IconBook
} from '@tabler/icons-react';

import App from './App';
import {AdminRoute, AdminDashboard} from './components/admin';
import AuthButton from './components/auth/AuthButton';
import CartButton from './components/cart/CartButton';
import {checkAuthStatus, fetchCSRFToken} from './store/slices/authSlice';
import {connectWebSocket} from './store/slices/websocketSlice';
import {loadCart} from './store/slices/cartSlice';
import BlogPage from './pages/BlogPage';
import BecomePrincessPage from './pages/BecomePrincessPage';
import PiggySubscriptionPage from './pages/PiggySubscriptionPage';
import RegisterPage from './pages/RegisterPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';

const Layout = ({children}) => {
  const [opened, {toggle, close}] = useDisclosure();
  const {isAuthenticated, user} = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    // Check authentication status on app mount
    dispatch(checkAuthStatus());
    // Fetch CSRF token for authenticated requests
    dispatch(fetchCSRFToken());
    // Connect WebSocket
    dispatch(connectWebSocket());
    // Load cart from localStorage
    dispatch(loadCart());
  }, [dispatch]);

  // Close navbar when route changes
  useEffect(() => {
    console.log("!!!! ephro path changed")
    close();
  }, [location.pathname, close]);

  console.log("!!! ephro opened", opened);

  return (
    <AppShell
      header={{height: 60}}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: {mobile: !opened},
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group h="100%" gap="xs" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <Text size={{base: 'md', sm: 'xl'}} fw={700} c="blue">
              PediPiggy
            </Text>
          </Group>
          <Group gap={{base: 4, sm: 'sm'}} wrap="nowrap">
            <CartButton/>
            <AuthButton/>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <NavLink
          component={Link}
          to="/"
          label="Home"
          leftSection={<IconHome size="1rem"/>}
        />

        <NavLink
          component={Link}
          to="/blog"
          label="Blog"
          leftSection={<IconBook size="1rem"/>}
        />

        <NavLink
          component={Link}
          to="/products"
          label="Browse Products"
          leftSection={<IconShoppingBag size="1rem"/>}
        />

        <NavLink
          component={Link}
          to="/princesses"
          label="Princesses"
          leftSection={<IconBuildingStore size="1rem"/>}
        />

        <NavLink
          component={Link}
          to="/cart"
          label="Shopping Cart"
          leftSection={<IconShoppingCart size="1rem"/>}
        />

        {isAuthenticated && !user?.is_seller && (
          <NavLink
            component={Link}
            to="/become-princess"
            label="Become a Princess"
            leftSection={<IconUserPlus size="1rem"/>}
          />
        )}

        {isAuthenticated && user?.is_seller && (
          <>
            <NavLink
              component={Link}
              to="/seller"
              label="Princess Dashboard"
              leftSection={<IconDashboard size="1rem"/>}
            />
            <NavLink
              component={Link}
              to="/seller/products"
              label="My Products"
              leftSection={<IconBoxSeam size="1rem"/>}
            />
            <NavLink
              component={Link}
              to="/seller/special-requests"
              label="Special Requests"
              leftSection={<IconSparkles size="1rem"/>}
            />
          </>
        )}

        {isAuthenticated && user?.is_staff && (
          <NavLink
            component={Link}
            to="/admin"
            label="Admin Dashboard"
            leftSection={<IconSettings size="1rem"/>}
          />
        )}

        {isAuthenticated && (
          <>
            <NavLink
              component={Link}
              to="/messages"
              label="Messages"
              leftSection={<IconMessage size="1rem"/>}
            />
            {(() => {
              console.log('User subscription check:', {
                user: user,
                subscription: user?.subscription,
                plan: user?.subscription?.plan,
                status: user?.subscription?.status,
                condition: user?.subscription?.plan === 'paypiggiepro' && user?.subscription?.status === 'active'
              });
              return user?.subscription?.plan === 'paypiggiepro' && user?.subscription?.status === 'active';
            })() && (
              <NavLink
                component={Link}
                to="/my-requests"
                label="My Special Requests"
                leftSection={<IconSparkles size="1rem"/>}
              />
            )}
            <NavLink
              component={Link}
              to="/subscription"
              label="Subscription"
              leftSection={<IconCrown size="1rem"/>}
            />
            <NavLink
              component={Link}
              to="/profile"
              label="Profile"
              leftSection={<IconUsers size="1rem"/>}
            />
          </>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        {children}
      </AppShell.Main>
    </AppShell>
  );
};

const Router = () => {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/*" element={<App/>}/>
          <Route
            path="/blog"
            element={<BlogPage/>}
          />
          <Route
            path="/become-princess"
            element={<BecomePrincessPage/>}
          />
          <Route
            path="/piggy-subscription"
            element={<PiggySubscriptionPage/>}
          />
          <Route
            path="/register"
            element={<RegisterPage/>}
          />
          <Route
            path="/verify-identity"
            element={<IdentityVerificationPage/>}
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard/>
              </AdminRoute>
            }
          />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
};

export default Router;
